import {Avatar, Banner, Button, DotProgress} from '@wandb/components';
import {type ReactNode, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {useAccountContext} from '../account/AccountProvider';
import {AccountType} from '../account/accountModel';
import {viewingAs} from '../admin/adminState';
import {redirectToIdentityLogin} from '../identity/loginRedirect';
import type {Surface} from '../surface';
import {
  authorizeEndpoint,
  type ConsentDetails,
  ConsentError,
  fetchConsent,
  LOGIN_ERROR,
  type OAuthParams,
  parseOAuthParams,
} from './cliLogin';
import {CLILoginLayout} from './CLILoginLayout';
import css from './CLILogin.module.css';
import {OrganizationField, type OrganizationOption} from './OrganizationField';

const CLILoginError = ({children}: {children: ReactNode}) => (
  <CLILoginLayout icon="lock-open">
    <Banner variant="error" isDismissible={false}>
      <Banner.Body>{children}</Banner.Body>
    </Banner>
  </CLILoginLayout>
);

/**
 * The session ended after the shell loaded, e.g. a logout in another tab.
 * Logging in again returns to this request.
 */
const SessionGone = ({
  message,
  origins,
}: {
  message: string;
  origins?: Partial<Record<Surface, string>>;
}) => {
  const location = useLocation();
  return (
    <CLILoginLayout icon="lock-open">
      <Banner variant="error" isDismissible={false}>
        <Banner.Body>{message}</Banner.Body>
      </Banner>
      <div className={css.actions}>
        <Button
          variant="primary"
          onPress={() =>
            redirectToIdentityLogin(
              origins,
              `${location.pathname}${location.search}`
            )
          }>
          Log in
        </Button>
      </div>
    </CLILoginLayout>
  );
};

/**
 * Consent page of the CLI's browser login. `wandb login` opens it with an
 * authorization request on the query string; the form posts that request, the
 * chosen organization, and a one-time consent token to Gorilla, which
 * redirects to the CLI's loopback server.
 */
export const CLILoginPage = ({
  origins,
}: {
  origins?: Partial<Record<Surface, string>>;
}) => {
  const location = useLocation();
  const params = useMemo(
    () => parseOAuthParams(location.search),
    [location.search]
  );

  if (params == null) {
    return (
      <CLILoginError>
        This page can only be opened by the W&B CLI. Run{' '}
        <code>wandb login</code> in your terminal to start again.
      </CLILoginError>
    );
  }

  // GraphQL carries the impersonation header; the consent fetch and the form
  // post do not, so Gorilla would issue the token to the admin's own session.
  if (viewingAs() !== '') {
    return (
      <CLILoginError>
        You cannot authorize the CLI while impersonating another user.
      </CLILoginError>
    );
  }

  // Keyed by the request so a new request starts with fresh state.
  return (
    <ConsentRequest key={location.search} params={params} origins={origins} />
  );
};

const ConsentRequest = ({
  params,
  origins,
}: {
  params: OAuthParams;
  origins?: Partial<Record<Surface, string>>;
}) => {
  const [consent, setConsent] = useState<ConsentDetails | null>(null);
  const [consentError, setConsentError] = useState<ConsentError | null>(null);
  const [chosenOrganization, setChosenOrganization] = useState<string | null>(
    null
  );
  const {
    viewer,
    accountList,
    selectedAccount,
    isLoading,
    error: accountError,
  } = useAccountContext();

  // Each call replaces the session's consent token, so fetch once per request.
  useEffect(() => {
    let cancelled = false;
    fetchConsent(params).then(
      details => {
        if (!cancelled) {
          setConsent(details);
        }
      },
      (err: ConsentError) => {
        if (!cancelled) {
          setConsentError(err);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [params]);

  if (consentError != null) {
    if (consentError.needsLogin) {
      return <SessionGone message={consentError.message} origins={origins} />;
    }
    return <CLILoginError>{consentError.message}</CLILoginError>;
  }

  if (consent == null || isLoading) {
    return (
      <CLILoginLayout icon="lock-open">
        <DotProgress aria-label="Loading CLI login" size="lg" />
      </CLILoginLayout>
    );
  }

  if (accountError != null || viewer == null) {
    return <CLILoginError>{LOGIN_ERROR}</CLILoginError>;
  }

  if (consent.organizations.length === 0) {
    return (
      <CLILoginError>
        You are not a member of any organization, so you cannot sign in to the
        CLI.
      </CLILoginError>
    );
  }

  const options = consent.organizations.map(
    name =>
      accountList.find(account => account.name === name) ?? unknownAccount(name)
  );
  const organization =
    [chosenOrganization, selectedAccount?.name, consent.organization].find(
      name => name != null && consent.organizations.includes(name)
    ) ?? consent.organizations[0];

  return (
    <CLILoginLayout icon="lock-open">
      <form action={authorizeEndpoint()} method="post" className={css.form}>
        {Object.entries(params).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
        <input
          type="hidden"
          name="consent_token"
          value={consent.consentToken}
        />
        <input type="hidden" name="organization" value={organization} />
        <div className={css.card}>
          <h1 className={css.heading}>Sign in to the W&B CLI</h1>
          <p className={css.subhead}>
            The W&B CLI will act on your behalf in the selected organization.
          </p>
          <div className={css.fields}>
            <section>
              <div className={css.user}>
                <div className={css.userInner}>
                  <div className={css.userAvatar}>
                    <Avatar
                      src={viewer.photoUrl ?? ''}
                      alt={`${viewer.username}'s profile picture`}
                    />
                  </div>
                  <div className={css.accountText}>
                    <span className={css.accountName}>{viewer.username}</span>
                    <span className={css.accountInfo}>{viewer.email}</span>
                  </div>
                </div>
              </div>
            </section>
            <section>
              <OrganizationField
                options={options}
                value={organization}
                onChange={setChosenOrganization}
              />
            </section>
          </div>
        </div>
        <div className={css.actions}>
          <Button type="submit" name="consent" value="deny" variant="secondary">
            Cancel
          </Button>
          <Button type="submit" name="consent" value="allow" variant="primary">
            Authorize
          </Button>
        </div>
      </form>
    </CLILoginLayout>
  );
};

/** Gorilla listed an organization the account query does not know yet. */
const unknownAccount = (name: string): OrganizationOption => ({
  name,
  accountType: AccountType.Corporate,
  memberCount: -1,
});
